import React, { useEffect, useState } from 'react';
import {
  Activity, AlertCircle, CheckCircle, Clock, Download, FileText, Loader2, Play, Save, Trash2, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { BACKEND_URL } from '../../config';

const SchedulerSection = () => {
  const [schedulerConfig, setSchedulerConfig] = useState(null);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [schedulerSaving, setSchedulerSaving] = useState(false);
  const [schedulerCsvFile, setSchedulerCsvFile] = useState(null);
  const [schedulerCsvUploading, setSchedulerCsvUploading] = useState(false);
  const [schedulerRunning, setSchedulerRunning] = useState(false);
  const [schedulerDraft, setSchedulerDraft] = useState({
    day_of_month: 20,
    hour: 3,
    minute: 0,
    recipient_email: 'jay@onetreehill.in',
    force_rescrape: false,
  });

  const fetchSchedulerConfig = async () => {
    try {
      setSchedulerLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/scheduler/config`);
      const data = await res.json();
      if (data.status === 'success') {
        setSchedulerConfig(data.config);
        setSchedulerDraft({
          day_of_month: data.config.day_of_month,
          hour: data.config.hour,
          minute: data.config.minute,
          recipient_email: data.config.recipient_email || 'jay@onetreehill.in',
          force_rescrape: !!data.config.force_rescrape,
        });
      } else {
        toast.error('Failed to load scheduler config');
      }
    } catch (e) {
      toast.error('Error loading scheduler: ' + e.message);
    } finally {
      setSchedulerLoading(false);
    }
  };

  const updateSchedulerConfig = async (patch) => {
    try {
      setSchedulerSaving(true);
      const res = await fetch(`${BACKEND_URL}/api/scheduler/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setSchedulerConfig(data.config);
        toast.success('Scheduler updated');
      } else {
        toast.error(data.detail || 'Failed to update scheduler');
      }
    } catch (e) {
      toast.error('Error saving scheduler: ' + e.message);
    } finally {
      setSchedulerSaving(false);
    }
  };

  const handleSchedulerToggle = (checked) => updateSchedulerConfig({ enabled: !!checked });

  const handleSchedulerDraftSave = () => {
    updateSchedulerConfig({
      day_of_month: Number(schedulerDraft.day_of_month),
      hour: Number(schedulerDraft.hour),
      minute: Number(schedulerDraft.minute),
      recipient_email: schedulerDraft.recipient_email,
      force_rescrape: !!schedulerDraft.force_rescrape,
    });
  };

  const handleForceRescrapeToggle = (checked) => {
    setSchedulerDraft((prev) => ({ ...prev, force_rescrape: !!checked }));
    updateSchedulerConfig({ force_rescrape: !!checked });
  };

  const handleSchedulerCsvUpload = async () => {
    if (!schedulerCsvFile) {
      toast.error('Choose a CSV file first');
      return;
    }
    try {
      setSchedulerCsvUploading(true);
      const fd = new FormData();
      fd.append('file', schedulerCsvFile);
      const res = await fetch(`${BACKEND_URL}/api/scheduler/csv`, { method: 'POST', body: fd });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setSchedulerConfig(data.config);
        setSchedulerCsvFile(null);
        const input = document.getElementById('scheduler-csv-input');
        if (input) input.value = '';
        toast.success(`CSV uploaded (${data.config.csv_row_count} PMs)`);
      } else {
        toast.error(data.detail || 'CSV upload failed');
      }
    } catch (e) {
      toast.error('Upload error: ' + e.message);
    } finally {
      setSchedulerCsvUploading(false);
    }
  };

  const handleSchedulerCsvDelete = async () => {
    if (!window.confirm('Delete the uploaded CSV? The scheduler will not run without it.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/scheduler/csv`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setSchedulerConfig(data.config);
        toast.success('CSV deleted');
      } else {
        toast.error(data.detail || 'Delete failed');
      }
    } catch (e) {
      toast.error('Delete error: ' + e.message);
    }
  };

  const handleSchedulerDownloadCsv = () => {
    window.open(`${BACKEND_URL}/api/scheduler/csv`, '_blank');
  };

  const handleSchedulerRunNow = async () => {
    if (!window.confirm('Run the scheduled scrape now for the PREVIOUS month? A report will be emailed after completion.')) return;
    try {
      setSchedulerRunning(true);
      const res = await fetch(`${BACKEND_URL}/api/scheduler/run-now`, { method: 'POST' });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        toast.success('Scrape started in background. Check Scraper Data for progress.');
      } else {
        toast.error(data.detail || 'Failed to trigger');
      }
    } catch (e) {
      toast.error('Trigger error: ' + e.message);
    } finally {
      setSchedulerRunning(false);
    }
  };

  useEffect(() => {
    fetchSchedulerConfig();
  }, []);

  return (
    <div className="space-y-6" data-testid="scheduler-section">
      {schedulerLoading && !schedulerConfig ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-600" />
        </div>
      ) : schedulerConfig && (
        <>
          {/* Automation Toggle Card */}
          <Card className="overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-indigo-600 to-blue-700">
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Monthly Auto-Scrape
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Automation</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {schedulerConfig.enabled
                      ? `Enabled — next run: ${schedulerConfig.next_run_at || 'calculating…'}`
                      : 'Disabled (default)'}
                  </div>
                </div>
                <Switch
                  checked={!!schedulerConfig.enabled}
                  onCheckedChange={handleSchedulerToggle}
                  disabled={schedulerSaving}
                  data-testid="scheduler-enabled-toggle"
                />
              </div>

              <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Force Re-scrape</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {schedulerConfig.force_rescrape
                      ? 'ON — rescrapes every PM in the CSV, even if data already exists'
                      : 'OFF (recommended) — skips PMs that already have data for the target month; saves credits'}
                  </div>
                </div>
                <Switch
                  checked={!!schedulerConfig.force_rescrape}
                  onCheckedChange={handleForceRescrapeToggle}
                  disabled={schedulerSaving}
                  data-testid="scheduler-force-rescrape-toggle"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Day of Month (1-28)</label>
                  <input
                    type="number"
                    min="1"
                    max="28"
                    value={schedulerDraft.day_of_month}
                    onChange={(e) => setSchedulerDraft((prev) => ({ ...prev, day_of_month: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="scheduler-day-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Hour (0-23, IST)</label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={schedulerDraft.hour}
                    onChange={(e) => setSchedulerDraft((prev) => ({ ...prev, hour: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="scheduler-hour-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Minute (0-59)</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={schedulerDraft.minute}
                    onChange={(e) => setSchedulerDraft((prev) => ({ ...prev, minute: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="scheduler-minute-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Report Email</label>
                  <input
                    type="email"
                    value={schedulerDraft.recipient_email}
                    onChange={(e) => setSchedulerDraft((prev) => ({ ...prev, recipient_email: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="scheduler-email-input"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={handleSchedulerDraftSave}
                  disabled={schedulerSaving}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="scheduler-save-btn"
                >
                  {schedulerSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Schedule
                </Button>
                <Button
                  onClick={handleSchedulerRunNow}
                  disabled={schedulerRunning || !schedulerConfig.csv_present}
                  variant="outline"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid="scheduler-run-now-btn"
                  title={!schedulerConfig.csv_present ? 'Upload a CSV first' : 'Runs the same job the cron would run'}
                >
                  <Play className="w-4 h-4 mr-1" />
                  Run Now (Test)
                </Button>
              </div>

              <Alert className="border-blue-200 bg-blue-50">
                <AlertCircle className="w-4 h-4 text-blue-600" />
                <AlertTitle className="text-blue-900">How it works</AlertTitle>
                <AlertDescription className="text-blue-800 text-sm">
                  On the chosen day (IST), the scheduler runs the uploaded CSV against the previous calendar month, then emails a summary + Excel scrape log to <b>{schedulerConfig.recipient_email}</b>. If a scrape is already running or no CSV is uploaded, the run is skipped with a notice email.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* CSV Card */}
          <Card className="overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-teal-600 to-emerald-700">
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5" />
                PM Portfolio Managers CSV
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {schedulerConfig.csv_present ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-sm font-semibold text-emerald-900">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        {schedulerConfig.csv_filename || 'scheduler.csv'}
                      </div>
                      <div className="text-xs text-emerald-700 mt-1">
                        {schedulerConfig.csv_row_count} PMs • uploaded {schedulerConfig.csv_uploaded_at ? new Date(schedulerConfig.csv_uploaded_at).toLocaleString() : '—'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleSchedulerDownloadCsv} data-testid="scheduler-csv-download-btn">
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSchedulerCsvDelete}
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        data-testid="scheduler-csv-delete-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <Alert className="border-amber-300 bg-amber-50">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <AlertTitle className="text-amber-900">No CSV uploaded</AlertTitle>
                  <AlertDescription className="text-amber-800 text-sm">
                    The scheduler won't run without a CSV of PM names. Upload one below.
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  id="scheduler-csv-input"
                  type="file"
                  accept=".csv"
                  onChange={(e) => setSchedulerCsvFile(e.target.files?.[0] || null)}
                  className="text-sm"
                  data-testid="scheduler-csv-file-input"
                />
                <Button
                  onClick={handleSchedulerCsvUpload}
                  disabled={!schedulerCsvFile || schedulerCsvUploading}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="scheduler-csv-upload-btn"
                >
                  {schedulerCsvUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                  {schedulerConfig.csv_present ? 'Replace CSV' : 'Upload CSV'}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                CSV format: a header row with <code>pm_name</code>, <code>Portfolio Manager</code>, <code>PM Name</code>, or <code>Name</code>, followed by one PM per row.
              </p>
            </CardContent>
          </Card>

          {/* Last Run Card */}
          <Card className="overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800">
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Last Run
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {!schedulerConfig.last_run_at ? (
                <p className="text-sm text-slate-500">No runs yet.</p>
              ) : (
                <div className="space-y-2 text-sm">
                  <div><b>When:</b> {new Date(schedulerConfig.last_run_at).toLocaleString()}</div>
                  <div>
                    <b>Status:</b>{' '}
                    <span className={
                      schedulerConfig.last_run_status === 'success' ? 'text-emerald-700 font-semibold'
                        : schedulerConfig.last_run_status === 'error' ? 'text-red-700 font-semibold'
                        : 'text-amber-700 font-semibold'
                    }>
                      {schedulerConfig.last_run_status}
                    </span>
                  </div>
                  {schedulerConfig.last_run_summary && (
                    <pre className="bg-slate-100 rounded p-3 text-xs overflow-auto">
                      {JSON.stringify(schedulerConfig.last_run_summary, null, 2)}
                    </pre>
                  )}
                  {schedulerConfig.last_run_error && (
                    <div className="text-red-700 text-xs bg-red-50 p-2 rounded border border-red-200">
                      Error: {schedulerConfig.last_run_error}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SchedulerSection;
